import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { getPublicDeletionStatus } from '../../services/authService';

/**
 * DeletionStatusPage — SCRUM-2266 (MVP 1.21.11.4)
 *
 * Public status page Meta reviewers (and users) visit to verify a
 * data-deletion request is progressing. Reads `id` and `code` from the
 * URL — both are required; either alone returns 404.
 *
 * Route: /deletion-status?id=...&code=...
 */
const STATUS_COPY = {
  requested: {
    title: 'Deletion requested',
    description: 'Your account data is scheduled for deletion. There is a 30-day grace period during which you can reclaim your account.',
  },
  processing: {
    title: 'Deletion in progress',
    description: 'Your account data is being removed. This can take a few hours.',
  },
  completed: {
    title: 'Deletion complete',
    description: 'Your account data has been removed.',
  },
  failed: {
    title: 'Deletion delayed',
    description: 'Deletion is taking longer than expected. Our team has been notified and will complete the process shortly.',
  },
  reclaimed: {
    title: 'Account restored',
    description: 'You reclaimed your account during the grace period. No data was deleted.',
  },
};

function formatDate(value) {
  if (!value) {
    return null;
  }
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

export default function DeletionStatusPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') || '';
  const code = searchParams.get('code') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!id || !code) {
      setError('Missing id or code in the URL.');
      setLoading(false);
      return undefined;
    }
    getPublicDeletionStatus(id, code)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setData(result);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setError('We could not find a deletion request matching this link.');
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, code]);

  const copy = data && STATUS_COPY[data.status]
    ? STATUS_COPY[data.status]
    : { title: 'Deletion status', description: '' };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full space-y-6">
          <h1 className="text-3xl font-bold text-gray-900">Account deletion status</h1>

          {loading && (
            <p className="text-gray-500">Loading…</p>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && data && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                <h2 className="text-xl font-semibold text-gray-900">{copy.title}</h2>
                <p className="text-gray-700 text-sm">{copy.description}</p>
              </div>

              <dl className="text-sm text-gray-700 space-y-1">
                {data.requestedAt && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Requested</dt>
                    <dd>{formatDate(data.requestedAt)}</dd>
                  </div>
                )}
                {data.gracePeriodEndsAt && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Expected completion</dt>
                    <dd>{formatDate(data.gracePeriodEndsAt)}</dd>
                  </div>
                )}
                {data.completedAt && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Completed</dt>
                    <dd>{formatDate(data.completedAt)}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
